import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { config } from '@/config';

/**
 * Session-expiry handling.
 *
 * The session is an HttpOnly cookie, so JS can neither read its expiry nor
 * refresh it. Before this module the only symptom of a lapsed session was a
 * 401 on every request, swallowed by whichever caller happened to make it —
 * the app kept rendering with stale data and no explanation.
 *
 * Rather than reject those requests (which would surface as a wall of error
 * toasts, or a redirect that discards whatever the user was in the middle of),
 * we *park* them: the promise stays pending, a re-auth dialog goes up over the
 * current page, and once the user signs back in every parked request is
 * replayed. Callers — React Query included — never observe a failure, so no
 * component unmounts and no unsaved form state is lost.
 *
 * Replay is safe because the backend's `AuthLayer` rejects before the handler
 * runs (`website-backend/src/controllers/middleware/auth.rs`), so a parked
 * request provably had no side effects. That is what makes this sound for
 * mutations and not just reads.
 */

/**
 * Requests parked longer than this are given up on. Swept when the next 401
 * arrives rather than on a timer — there is no benefit to rejecting early
 * while the dialog is still up and re-auth would still resolve them.
 */
const MAX_PARK_MS = 10 * 60 * 1000;

/**
 * Upper bound on the queue so a tab left open against a dead session can't
 * grow it without limit. Oldest entries are evicted (rejected) first.
 */
const MAX_PARKED = 50;

interface ParkedRequest {
  config: InternalAxiosRequestConfig;
  error: AxiosError;
  parkedAt: number;
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
}

type Listener = () => void;

let expired = false;
let parked: ParkedRequest[] = [];
let probeInFlight: Promise<void> | null = null;
/** Set by `installSessionExpiryInterceptor`; see `InstallOptions.isAuthenticated`. */
let authenticated: () => boolean = () => false;
const listeners = new Set<Listener>();

/**
 * `withCredentials`-carrying requests that legitimately answer 401 for reasons
 * that have nothing to do with an expired session: a wrong password on
 * `/login`, an unauthenticated probe of `/current`, a spent reset token. These
 * must reach their caller untouched.
 */
function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  try {
    // `url` may be relative; the base only matters for parsing.
    return new URL(url, window.location.origin).pathname.startsWith('/api/auth/');
  } catch {
    return url.includes('/api/auth/');
  }
}

function emit() {
  listeners.forEach((listener) => { listener(); });
}

/** Subscribe to expiry-state changes. Returns an unsubscribe function. */
export function subscribeToSessionExpiry(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** Current expiry state — the snapshot half of a `useSyncExternalStore` pair. */
export function isSessionExpired(): boolean {
  return expired;
}

/**
 * Flag the session as expired. Idempotent, and safe to call from anywhere that
 * learns the session is gone (the axios interceptor, the `currentUser` refetch,
 * a rejected WebSocket upgrade).
 */
export function markSessionExpired(): void {
  if (expired) return;
  expired = true;
  emit();
}

function settleAll(settle: (entry: ParkedRequest) => void) {
  const entries = parked;
  parked = [];
  entries.forEach(settle);
}

/**
 * Called once the user has re-authenticated: replay every parked request so its
 * original caller resolves as though the interruption never happened.
 */
export async function resumeAfterReauth(client: AxiosInstance = axios): Promise<void> {
  expired = false;
  emit();

  const entries = parked;
  parked = [];

  await Promise.all(
    entries.map(async (entry) => {
      // React Query aborts on unmount; a replay would fail anyway, and the
      // caller is gone regardless.
      if (entry.config.signal?.aborted) {
        entry.reject(entry.error);
        return;
      }
      try {
        entry.resolve(await client.request(entry.config));
      } catch (err) {
        entry.reject(err);
      }
    }),
  );
}

/**
 * Give up on the expired session: reject everything parked and clear the flag.
 * Used by the "sign out instead" path, which navigates away immediately.
 */
export function abandonSession(): void {
  expired = false;
  settleAll((entry) => { entry.reject(entry.error); });
  emit();
}

/**
 * Ask the API whether the session is still good, and flag expiry if it isn't.
 *
 * For signals that aren't themselves HTTP responses — chiefly a WebSocket
 * upgrade the browser reports only as an opaque 1006 close. Deduped, because
 * those signals arrive in bursts and one answer serves all of them. Only a 401
 * counts: a network failure means we don't know, and guessing "expired" would
 * throw a re-auth dialog at someone who merely went offline.
 */
export function probeSession(client: AxiosInstance = axios): Promise<void> {
  probeInFlight ??= client
    .get(`${config.backendApi}/api/auth/current`, { withCredentials: true })
    .then(
      () => undefined,
      (err: unknown) => {
        if (axios.isAxiosError(err) && err.response?.status === 401 && authenticated()) {
          markSessionExpired();
        }
      },
    )
    .finally(() => {
      probeInFlight = null;
    });
  return probeInFlight;
}

/** Test-only reset so state doesn't leak between cases. */
export function resetSessionExpiryForTests(): void {
  expired = false;
  parked = [];
  probeInFlight = null;
  authenticated = () => false;
  listeners.clear();
}

/** Number of currently parked requests. Exposed for tests and diagnostics. */
export function parkedRequestCount(): number {
  return parked.length;
}

function evictStale(now: number) {
  const live: ParkedRequest[] = [];
  for (const entry of parked) {
    if (now - entry.parkedAt > MAX_PARK_MS) {
      entry.reject(entry.error);
    } else {
      live.push(entry);
    }
  }
  parked = live;

  while (parked.length >= MAX_PARKED) {
    const oldest = parked.shift();
    oldest?.reject(oldest.error);
  }
}

interface InstallOptions {
  /**
   * Whether we currently believe someone is signed in. A 401 only means
   * "expired" if we thought we had a session; for a signed-out visitor it is
   * the normal answer and the route guards already handle it.
   */
  isAuthenticated: () => boolean;
  /** Defaults to the global axios default export, which every call site uses. */
  client?: AxiosInstance;
}

/**
 * Install the response interceptor. Returns an eject function.
 *
 * Registered at module scope (not in an effect) so it is in place before the
 * first request leaves the app.
 *
 * **A 401 is not taken as proof that the session is dead.** The API returns 401 for a
 * great many things that have nothing to do with authentication — "No active context",
 * "Team not in your org", "Tag definition not found" — so trusting the status alone
 * showed people a password prompt for a missing header or a resource they could not see.
 *
 * Worse, it could not be escaped: `resumeAfterReauth` replays parked requests through this
 * same client, and a request that 401'd because it had no active context still has no
 * active context on replay. It 401s again, the session is marked expired again, and the
 * dialog reopens. Entering the correct password never cleared it; only signing out did.
 *
 * So an unexpected 401 is now checked against `/api/auth/current` before anything is
 * claimed. If the session answers, the 401 meant something else and is passed through to
 * the caller untouched. The cost is one extra round trip on the first surprising 401 —
 * deduped across a burst by `probeInFlight`, and skipped entirely once expiry is known.
 */
export function installSessionExpiryInterceptor({ isAuthenticated, client = axios }: InstallOptions): () => void {
  authenticated = isAuthenticated;

  const id = client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const requestConfig = error.config;

      if (
        error.response?.status !== 401 ||
        !requestConfig ||
        isAuthEndpoint(requestConfig.url) ||
        !isAuthenticated()
      ) {
        return Promise.reject(error);
      }

      // Once expiry is established there is nothing left to confirm, and re-probing per
      // request would put a round trip in front of every parked call.
      //
      // Read through `isSessionExpired()` rather than the module variable: TypeScript
      // narrows a `let` across an `await` and cannot see that `probeSession` sets it, so
      // the second check would be flagged as always-true and is not.
      if (!isSessionExpired()) {
        await probeSession(client);
        if (!isSessionExpired()) {
          // The session is fine, so this 401 was about something else — a missing context
          // header, a resource in another tenant, a permission the user lacks. Hand it
          // back so the caller can render whatever it means.
          return Promise.reject(error);
        }
      }

      return new Promise((resolve, reject) => {
        const now = Date.now();
        evictStale(now);
        parked.push({ config: requestConfig, error, parkedAt: now, resolve, reject });
      });
    },
  );

  return () => { client.interceptors.response.eject(id); };
}
